import Head from 'next/head'
import { OttProviders } from '../../Ott/Components/Providers'
import { Scripts } from '../../Ott/Components/Scripts'
import { Navigation } from '../../Ott/Components/Navigation/Navigation'
import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { mediaCdn, utils } from '../../components/Player/Helpers'
import { useRouter } from 'next/router'
import * as process from 'process'
import { config } from '../../lib'
import { clientConfig } from '../../client'
import { he } from '../../locales/he'
import {
  defaultGoogleAdsConfig,
  IConfigFile,
  ILiveSources,
  IOttPageMeta,
  liveSourcesDefault,
  prettyLog,
} from '../../utils'
import {
  CategoryTree,
  getActivePage,
  getDataForProgram,
  ServeByDevice,
} from '../../Ott/Api'
import {
  ICategoryTreeItem,
  Ileafs,
  IOttWebApp,
  IServeByDevice,
} from '../../types'
import ViewLayout from '../../Ott/Components/View/ViewLayout'
import { getKs, ServerAssetData, ServerFileUtils } from '../../utils/server'
import {
  AutoRefresh,
  DebugConsoleLog,
  IdxMetaTags,
  OttAdminBarLoader,
  PageLoader,
  RedAlert,
} from '../../sharedComponents'
import { asset_type_enum, isCatchUpAsset } from '../../Ott/types'
import BreadcrumbsJsonLd from '../../Ott/Components/seo/BreadcrumbsJsonLd'
import dynamic from 'next/dynamic'

const LiveBroadcastJsonLd = dynamic(() => {
  return import('../../Ott/Components/seo/BroadcastEventLive')
})
const LiveBBVideoObjectJsonLd = dynamic(() => {
  return import('../../Ott/Components/seo/LiveBBVideoObjectJsonLd')
})
const VideoObjectJsonLd = dynamic(() => {
  return import('../../Ott/Components/seo/VideoObjectJsonLd')
})

const OttLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(
    90deg,
    #06060d -34.07%,
    #0c0f1f 47.01%,
    #06060d 137.67%
  );
  direction: rtl;

  @media only screen and (max-width: 768px) {
    background: #06060d
      linear-gradient(90deg, #06060d -34.07%, #161a2d 47.01%, #06060d 137.67%);
  }

  @media only screen and (min-width: 2000px) {
    padding: 0 calc((100vw - 1950px) / 2);
    box-sizing: border-box;
  }
`

const noIndexRegex = /^\/.*\/public\/cdn(\/.*)?$/

function OttWebApp({
  logo,
  adTargetEnv,
  adblock_detection,
  kalturaClientTag,
  liveSources,
  login_page,
  bigBrotherRegistration,
  kaltura_partner_id,
  kaltura_end_point,
  kaltura_application_name,
  uiconf_id,
  tree,
  searchPageId,
  hpId,
  allProgramsID,
  activePage,
  leafs,
  minuteLyData,
  program,
  assetMeta,
  configFile,
  page_type,
}: IOttWebApp) {
  // const [ottPageTitle, setOttPageTitle] = useState<string>('')
  // const { pageTitle, pageType } = useRootStore()
  const { asPath } = useRouter()
  config.adTargetEnv = adTargetEnv
  // Runtime kill-switch for AdBlock detection (RESH13-1075): this page doesn't
  // go through setClientConfig() like the main web app, so it must set the
  // shared clientConfig singleton itself for isAdBlockDetectionEnabled() to see it.
  clientConfig.adblock_detection = adblock_detection || 'enabled'
  config.kalturaClientTag = kalturaClientTag
  config.liveSources = liveSources
  config.login_page = login_page
  config.bigBrotherRegistration = bigBrotherRegistration
  config.kaltura_partner_id = kaltura_partner_id
  config.kaltura_end_point = kaltura_end_point
  config.kaltura_application_name = kaltura_application_name
  config.uiconf_id = uiconf_id || 50272542
  config.searchPageId = searchPageId
  config.hpId = hpId
  config.allProgramsID = allProgramsID
  config.activePage = activePage
  config.configFile = { ...config.configFile, ...configFile }
  const liveBBPage = '2010268'
  const liveBroadcastPage = '2010263'
  const title = useMemo(
    () => assetMeta.seo.title || assetMeta.title,
    [assetMeta.seo.title, assetMeta.title]
  )
  useEffect(() => {
    if (!asPath.includes('[[...all]')) {
      utils.setPageViewedInSession(utils.getPageViewedInSession() + 1)
      window.dmp?.run() // used for IDX need to run on every page change
      window._heyDayInterface?.methods.activateSearchBox() // used for heyday need to run on every page change
    }
  }, [asPath])

  const LiveBroadcastJsonLdComponent = () => {
    const { asPath } = useRouter()
    return asPath.includes(liveBroadcastPage) ? (
      <LiveBroadcastJsonLd config={configFile} />
    ) : null
  }

  const LiveBBVideoObjectJsonLdComponent = () => {
    const { asPath } = useRouter()
    return asPath.includes(liveBBPage) ? (
      <LiveBBVideoObjectJsonLd assetMeta={assetMeta} />
    ) : null
  }

  const VideoObjectJsonLdComponent = () => {
    const { asPath } = useRouter()
    return !asPath.includes(liveBBPage) &&
      !asPath.includes(liveBroadcastPage) ? (
      <VideoObjectJsonLd
        program={program}
        logo={logo}
        asPath={asPath}
        assetMeta={assetMeta}
      />
    ) : null
  }

  // Matches the main site's HeadTags: query strings are not part of the
  // canonical URL, and the OTT route serves the same asset under
  // /allshows/<id>/ and /allshows/embed/<id>/.
  const canonical = useMemo(
    () => `https://13tv.co.il${asPath.split('?')[0]}`,
    [asPath]
  )

  // An OTT page is either a video - a catch-up program, an episode, a movie,
  // the live channel - or a browsing screen.
  const ogType = useMemo(() => {
    switch (page_type) {
      case 'program':
      case 'Episodes':
        return 'video.episode'
      case 'Movies':
        return 'video.movie'
      case 'Linear':
      case 'Trailer':
        return 'video.other'
      default:
        return 'website'
    }
  }, [page_type])

  const headLevels = useMemo(() => {
    const levels = asPath.split('/')
    return levels.filter(l => !!l).join(',')
  }, [asPath])

  if (asPath.includes('[[...all]')) {
    return null
  }
  return (
    <React.Fragment key={'OttWebApp'}>
      <OttProviders
        program={program}
        theTree={tree}
        leafs={leafs}
        assetMeta={assetMeta}
        logo={logo}
        page_type={page_type}
        googleAdsConfig={configFile.googleAds}
      >
        <Head key={'head'}>
          <title>{title}</title>
          <meta
            name='description'
            content={assetMeta.seo.description || assetMeta.description}
          />
          <meta name='page-levels' content={headLevels} />
          <meta name='canRecommend' content='false' />
          <meta
            name='robots'
            content={
              noIndexRegex.test(asPath)
                ? 'noindex, nofollow, nosnippet'
                : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
            }
          />
          <meta
            property='og:title'
            content={assetMeta.seo.title || assetMeta.title}
          />
          <meta
            property='og:description'
            content={assetMeta.seo.description || assetMeta.description}
          />
          <meta property='og:image' content={assetMeta.ogImage} />
          {assetMeta.ogImage && (
            <meta property='og:image:width' content='1200' />
          )}
          {assetMeta.ogImage && (
            <meta property='og:image:height' content='630' />
          )}
          <meta property='og:url' content={canonical} />
          <meta property='og:type' content={ogType} />
          <meta
            property='og:site_name'
            content={configFile?.siteName || he.site_name}
          />
          <meta property='og:locale' content='he_IL' />
          {minuteLyData?.gridTitle && (
            <meta name='grid_title' content={minuteLyData.gridTitle} />
          )}
          {minuteLyData?.publishDate && (
            <meta name='publishDate' content={minuteLyData.publishDate} />
          )}
          <meta
            name='minutely_reels'
            content={minuteLyData?.promoteOnMinuteLy ? 'true' : 'false'}
          />
          <link rel='canonical' href={canonical} />
        </Head>
        <IdxMetaTags title={title} path={asPath} />
        <BreadcrumbsJsonLd
          configFile={configFile}
          program={program}
          asPath={asPath}
        />
        {VideoObjectJsonLdComponent()}
        {LiveBroadcastJsonLdComponent()}
        {LiveBBVideoObjectJsonLdComponent()}
        <Scripts key={'ott_scripts'} />
        <OttLayout>
          <OttAdminBarLoader key={'OttAdminBar'} />
          <PageLoader key={'pageLoader-ott'} />
          <RedAlert key={'RedAlert'} />
          <Navigation key={'Navigation'} />
          <ViewLayout configFile={configFile} key={'ViewLayout'} />
        </OttLayout>
        <AutoRefresh />
        <DebugConsoleLog />
      </OttProviders>
    </React.Fragment>
  )
}

export const getStaticProps = async (context: {
  params: { all: string[] }
}) => {
  let page_type = 'screen'
  const path = (context.params?.all as string[]) ?? []
  const kaltura = {
    end_point:
      process.env.KALTURA_OTT_END_POINT ||
      'https://5031.frp1.ott.kaltura.com/api_v3/service/',
    partner_id: process.env.KALTURA_PARTNER_ID || '5031',
    udid: 'reshet_13_server',
    application_name:
      process.env.KALTURA_APPLICATION_NAME || 'com.kaltura.reshet.web',
  }

  let liveSources = { ...liveSourcesDefault }
  try {
    const sourcesFromFile = await ServerFileUtils.readJsonFile({
      filePath: './public/cdn/live.json',
    })
    if (sourcesFromFile.success) {
      liveSources = sourcesFromFile.data as ILiveSources
    }
  } catch (e) {
    console.log('failed /cdn/live.json fetch')
  }

  let configFile: IConfigFile = {
    ros_interstitial: 'off',
    recently_aired_json_path: './public/cdn/recently_aired.json',
    live_title: 'ערוץ 13 שידור חי - לייב - צפייה ישירה',
    live_h_title: 'עכשיו בערוץ 13 - שידור חי',
    live_description:
      'ערוץ 13 LIVE לצפייה ישירה. שידור חי רשת 13 - צפו בכל התכניות של רשת 13 וחדשות 13 בשידור חי.',
    siteName: '',
    logo: mediaCdn + 'v1746345448/13_q8gehw.webp',
    short_videos_client: '/cdn/short_clips_and_tabs/',
    short_videos_server: './public/cdn/short_clips_and_tabs/',
    bb_ads_time_interval: 0,
    video_rail_map_path: '',
    player: { autoplay: { mobile: false, desktop: true, app: false } },
    googleAds: defaultGoogleAdsConfig,
  }

  try {
    const configFromFile = await ServerFileUtils.readJsonFile({
      filePath: './public/cdn/config.json',
    })
    if (configFromFile.success) {
      configFile = {
        ...configFile,
        ...(configFromFile.data as IConfigFile),
      }
    }
  } catch (e) {
    console.log('failed /cdn/config.json' + ' fetch', e)
  }

  const { ks } = await getKs(kaltura)

  let config = {} as IServeByDevice
  try {
    config = await ServeByDevice({ ...kaltura })
  } catch (e) {
    prettyLog('[[...all]]: 181', { e })
  }

  const root = config.params.Categories.Root
  const logo =
    configFile?.logo ||
    mediaCdn + 'q_auto:best/v1702818913/dev-uploads/2023/861966.webp'
  const tree = (await CategoryTree({
    kaltura_end_point: kaltura.end_point,
    root: root,
    ks,
  })) as ICategoryTreeItem[]

  const hpId =
    tree.find((ch: ICategoryTreeItem) => ch.name === 'ראשי')?.id || tree[0].id

  const allProgramsID = tree.find(
    (ch: ICategoryTreeItem) => ch.name === 'כל התוכניות'
  )?.id

  const searchPageId =
    tree.find((ch: ICategoryTreeItem) => ch.name === 'חיפוש')?.id || null

  const activePage = getActivePage({ hpId, path })

  const leafs = [] as Ileafs[]

  let asset: IOttPageMeta = {
    assetId: 0,
    title: he.site_name,
    description: '',
    ogImage: '',
    ref: '',
    externalId: '',
    seo: {
      title: '',
      description: '',
    },
    seasonNumber: 0,
    type: 0,
    startDate: 0,
    endDate: 0,
  }
  if (activePage === 'player') {
    try {
      asset = await ServerAssetData.fetchData({ path: path[0], ks, configFile })
      if (asset.type === asset_type_enum.NotFound) {
        page_type = '404'
      } else if (asset.type === asset_type_enum.Live) {
        page_type = 'Linear'
      } else if (asset.type === asset_type_enum.Program) {
        // 'program' matches the page_type the player reports for catch-up.
        page_type = isCatchUpAsset(asset) ? 'program' : 'Linear'
      } else {
        page_type =
          Object.keys(asset_type_enum).find(
            key =>
              asset_type_enum[key as keyof typeof asset_type_enum] ===
              asset.type
          ) || 'screen'
      }
    } catch (e) {
      prettyLog('[[...all]]: 300', { msg: `failed asset: ${path[0]} fetch`, e })
    }
  } else {
    const branch = tree.find(b => b.id === activePage)

    if (branch) {
      asset.title =
        branch.name === '' || branch.name === 'ראשי'
          ? he.ott_main_name
          : [branch.name, ' | ', he.site_name].join('')

      const { leafs: processedLeafs } =
        await ServerAssetData.processRailedPageMapping({
          branch,
          ks,
          kaltura_end_point: kaltura.end_point,
          allProgramsID: parseInt(allProgramsID + ''),
          activePage,
          isHp: activePage === hpId,
        })

      leafs.push(...processedLeafs)
    }
  }
  let seriesHp: null | string = null
  const {
    seriesId,
    seriesName,
    season,
    seasonsList,
    episodes,
    episode,
    programData,
    totalCount,
  } = await getDataForProgram({
    path,
    ks,
  })

  if (seriesId) {
    seriesHp = await ServerAssetData.fetchSeriesHP({
      seriesId,
      short_videos_path:
        configFile?.short_videos_server + '' ||
        './public/cdn/short_clips_and_tabs/',
    })
  }

  if (programData) {
    let episodeData
    if (episode) {
      episodeData = episodes.find(e => e.id === episode)
    } else {
      episodeData = episodes[0]
    }

    asset.title = asset.seo.title = ServerAssetData.setTitle({
      path,
      configFile,
      programData,
      episodeData,
      airDate: isCatchUpAsset(asset) ? asset.startDate : undefined,
    })

    asset.description = asset.seo.description = episodeData
      ? episodeData.description
      : programData.description

    try {
      asset.ogImage =
        (
          programData.images.find(img => img.ratio === '16x9') ||
          programData.images?.pop()
        )?.url + '/width/1200/height/630/quality/75'
    } catch (e) {
      asset.ogImage = `${mediaCdn}q_auto:best/v1750236296/assets/2025/default.webp`
    }

    if (episodeData) {
      asset.assetId = episodeData?.id || 0
      asset.seasonNumber = episodeData?.metas.SeasonNumber || 0
      asset.externalId = episodeData.externalId
    } else {
      if (activePage !== 'player') {
        page_type = '404'
      }
    }
  }

  const revalidate = process.env.REVALIDATE_IN_SECONDS
    ? parseInt(process.env.REVALIDATE_IN_SECONDS, 10)
    : 300

  // if (season && path[3] && path[3].length <= 3) {
  //   const from = path.join('/')
  //   path[3] = season.id + ''
  //   const to = path.join('/')
  //   console.log(`Redirecting from ${from} to ${to}`)
  //   return {
  //     redirect: {
  //       destination: ottRoot + '/' + to,
  //       permanent: true, // Default to permanent (301) if not specified
  //       // statusCode:
  //       //   page.PageMeta.redirect.statusCode ||
  //       //   (page.PageMeta.redirect.isPermanentRedirect ? 301 : 302),
  //     },
  //     // Even with redirects, we should set a revalidate time
  //     revalidate: revalidate,
  //   }
  // }

  let minuteLyData = null
  if (asset.assetId > 0) {
    minuteLyData = await ServerAssetData.fetchMinuteLyData(
      asset.assetId,
      asset.ref || asset.externalId,
      path
    )
  }

  return {
    notFound: page_type === '404',
    props: {
      logo,
      adTargetEnv: process.env.AD_TARGET_ENV || '',
      adblock_detection:
        (process.env.ADBLOCK_DETECTION as 'enabled' | 'disabled') || 'enabled',
      kalturaClientTag: process.env.KALTURA_CLIENT_TAG,
      liveSources: liveSources,
      configFile,
      login_page: process.env.LOGIN_PAGE,
      bigBrotherRegistration: process.env.BIG_BROTHER_REGISTRATION,
      kaltura_end_point: kaltura.end_point,
      kaltura_partner_id: kaltura.partner_id,
      kaltura_application_name: kaltura.application_name,
      uiconf_id: config.params.PlayerSettings.uiconf_id,
      tree,
      searchPageId,
      hpId,
      allProgramsID,
      activePage,
      leafs,
      assetMeta: asset,
      minuteLyData,
      page_type,
      program: {
        seriesId,
        seriesName,
        seriesHp,
        season,
        seasonsList,
        episodes,
        episode,
        programData,
        totalCount,
      },
    },
    revalidate: revalidate,
  }
}
export const getStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

// OttWebApp.getInitialProps = () => {
//   return {
//     adTargetEnv: process.env.AD_TARGET_ENV,
//     kalturaClientTag: process.env.KALTURA_CLIENT_TAG,
//   }
// }
export default OttWebApp
